/**
 * Client helper for invoking per-user privileged callables through the
 * server proxy (`POST /api/functions/:name`) rather than hitting the
 * Cloud Run callable directly.
 *
 * WHY THIS EXISTS
 * ---------------
 * This GCP org enforces Domain Restricted Sharing: granting
 * `allUsers` / `allAuthenticatedUsers` the Cloud Run `run.invoker` role
 * is forbidden project-wide. Without that grant, the Cloud Run edge
 * rejects a browser's direct callable invocation ("The request was not
 * authenticated … Empty Authorization header value"), which surfaces in
 * the UI as a generic `INTERNAL` toast.
 *
 * The server proxy runs as a permitted service account, verifies the
 * caller's Firebase ID token (`adminAuth.verifyIdToken` → `uid`), and
 * re-implements the privileged operation server-side. This helper is the
 * client half of that contract for *per-user* (non-admin) operations —
 * the chat assistant, insight feedback, and DAM input proposals.
 *
 * Distinct from `callAdminFunction` (src/components/admin/adminApi.ts),
 * which targets the same proxy but is only reachable by platform admins.
 *
 * Returns the unwrapped `result` payload (the proxy responds with
 * `{ result }`, mirroring the shape `httpsCallable` returns under `.data`).
 */
import { authedFetch } from './authedFetch';

export class UserFunctionError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'UserFunctionError';
  }
}

/**
 * Invoke `/api/functions/<name>` with the signed-in user's bearer token.
 * `authedFetch` attaches the Firebase ID token automatically.
 *
 * @throws UserFunctionError when the server responds non-2xx — the
 *         message mirrors the server's `{ error }` body so the existing
 *         toast surfaces the real cause instead of a generic failure.
 */
export async function callUserFunction<T = unknown>(
  name: string,
  data: unknown = {},
): Promise<T> {
  const res = await authedFetch(`/api/functions/${name}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ data }),
  });

  const json = (await res.json().catch(() => null)) as
    | { result?: T; error?: unknown; detail?: unknown; message?: unknown }
    | null;

  if (!res.ok) {
    const errObj = json?.error;
    const base =
      (typeof errObj === 'string' && errObj)
      || (errObj && typeof (errObj as { message?: unknown }).message === 'string'
        ? (errObj as { message: string }).message
        : null)
      || (typeof json?.message === 'string' && json.message)
      || `Function ${name} failed (${res.status})`;
    const detail = typeof json?.detail === 'string' ? ` ${json.detail}` : '';
    throw new UserFunctionError(`${base}${detail}`, res.status);
  }

  return (json?.result ?? json) as T;
}
